import { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react'
import { lightTheme } from '@/customTheme'
import useAppContext from '@/hooks/useAppContext'
import useModal from '@/hooks/useModal'
import { styled, ThemeProvider } from 'baseui'
import ModalUnsuportType from './ModalUnsuportType'
import IntroVideo from '../IntroVideo'
import ModalDiscardProject from './ModalDiscardProject'
import ModalDeleteProject from './ModalDeleteProject'
import { ShowModalType } from '@/scenes/engine/common/constants'
import ModalProjectOpenFailed from './ModalProjectOpenFailed'
import ModalProjectAlreadyOpen from './ModalProjectAlreadyOpen'
import ModalConfirmSignOut from './ModalConfirmSignOut'
import { useSelector } from 'react-redux'
import { selectOpenModalTryPremium, selectUser, selectUserIsPremium } from '@/store/slices/user/selectors'
import { setOpenModalTryPremium } from '@/store/slices/user/actions'
import modalStyles from '@/scenes/Editor/components/Modal/Modal.module.css'
import ScaledAppearanceModal from '@/scenes/Editor/components/Modal/ScaledAppearanceModal'
import { SignInManager } from '@/scenes/Editor/components/Navbar/components/SignInManager'
import { UserInfoStorage } from '@/utils/userInfoStorage'
import SignIn from '@/scenes/Editor/components/Navbar/components/SignIn/SignIn'
import { getLocalStorage } from '@/utils/localStorage'
import ModalNoInternet from './ModalNoInternet'
import { customAmplitude } from '@/utils/customAmplitude'
import { useAppDispatch } from '@/store/store'
import { useLocation } from 'react-router-dom'
import Metsikon from '@/scenes/Editor/components/Navbar/components/SignIn/Metsikon/Metsikon'
import ModalAILimitReached from './ModalAILimitReached'
import { useTour } from '@reactour/tour'
import ModalCustomText from './ModalCustomText'
import ModalLimitedTimeOffer from './ModalLimitedTimeOffer'
import ModalLimitedOffer from './LimitedOffer/ModalLimitedOffer'
import { Z_INDEX } from '@/constants/z-index'
import useInitialModalFlow, { isStartupPremiumPromptSuppressed } from '@/hooks/useInitialModalFlow'
import useUserActivityTracking from '@/hooks/useUserActivityTracking'
import ModalWhatNew from './ModalWhatNew'
import { buildPremiumPromptProperties, buildSignupAnalyticsContext } from '@/utils/signupPremiumAnalytics'
import { isPartnerRoute } from '@/constants/routes'
import { BATCH_STUDIO_ROUTE } from '@/scenes/Dashboard/BatchEdit/batchRoutes'

const ModalDownloadProject = lazy(() => import('./ModalDownloadProject'))
const ModalDownloadApp = lazy(() => import('./ModalDownloadApp'))
const ModalTutorial = lazy(() => import('./ModalTutorial'))
const Onboarding = lazy(() => import('./Onboarding'))
const ModalMagicDesign = lazy(() => import('./ModalMagicDesign'))
const USE_ANIMATIONS = false

const WrapModal = styled('div', (props: any) => ({
  position: 'absolute',
  inset: 0,
  background: 'rgba(0,0,0,0.5)',
  backdropFilter: 'blur(5px)',
  display: 'flex',
  justifyContent: 'center',
  overflow: 'auto',
  alignItems: 'center',
  '::-webkit-scrollbar': {
    width: '8px',
    margin: '20px 0',
  },
  '::-webkit-scrollbar-track': {
    borderRadius: '4px',
  },
  '::-webkit-scrollbar-thumb': {
    borderRadius: '4px',
    backgroundColor: lightTheme.colors.grayScale100,
    minHeight: '100px ',
  },
  '::-webkit-scrollbar-thumb:hover': {
    cursor: 'pointer',
    backgroundColor: lightTheme.colors.grayScale150,
  },
  '@media screen and (max-height: 652px)': {
    alignItems: 'start',
  },
  ...props.style,
}))

function ShowModal() {
  const { isOpenPixelManipulationObject, isLoadedJsonSuccess, setIsOpenTutorial, isOpenTutorial } = useAppContext()
  const { showModalType, setShowModalType } = useModal()
  const openModalTryPremium = useSelector(selectOpenModalTryPremium)
  const userIsPremium = useSelector(selectUserIsPremium)
  const [isLoggined, setIsLoggined] = useState(false)
  const user = useSelector(selectUser)
  const dispatch = useAppDispatch()
  const location = useLocation();

  const { setIsOpen, setSteps } = useTour()
  // Persists selected plan + cached pricing across Metsikon unmount/remount (survives sign-in redirect)
  const pendingPlanRef = useRef<{ plan: number; planDetail: any; planType?: 'premium' | 'super' } | null>(null)

  useInitialModalFlow()
  useUserActivityTracking()

  useEffect(() => {
    setIsLoggined(!!user)
  }, [user])

  useEffect(() => {
    function getUser() {
      let userInfo = UserInfoStorage.get()
      if (userInfo) {
        setIsLoggined(true)
    }
    getUser()
  }}, [])

  const handleOpenTutorial = () => {
    // Re-open remove tutorial step 3 for non-premium users (anonymous or signed-in free)
    // when the premium modal is dismissed without an upgrade.
    if(isOpenPixelManipulationObject && isOpenTutorial && !userIsPremium) {
      setIsOpen(true)
      return
    }
    // Fully close the tutorial when the user is premium (e.g. after upgrade success).
    if(isOpenPixelManipulationObject && userIsPremium) {
      setIsOpenTutorial(false)
      setShowModalType(null)
      setIsOpen(false)
      setSteps([])
    }
    const isCreatePath =
      location.pathname.includes('/create/ai-models') || location.pathname.includes('/create/photoshoots')
    // Batch Studio has no editor canvas, so the remove-object tutorial can't run there.
    const isBatchPath = location.pathname.startsWith(BATCH_STUDIO_ROUTE)
    if (isCreatePath || isBatchPath) {
      return
    }
    let showedRemoveObjectTutorial = getLocalStorage('showedRemoveObjectTutorial') ? JSON.parse(getLocalStorage('showedRemoveObjectTutorial')) : false
    if(!showedRemoveObjectTutorial || !showedRemoveObjectTutorial.isOpened) {
      setShowModalType(ShowModalType.QUICK_TUTORIAL)
    } 
  }

  const closeSignIn = useCallback(() => setShowModalType(null), [setShowModalType])

  /**
   * On the partner landing page, sign-in is a step inside a purchase the visitor
   * started by picking a plan — not a gate the app put in front of them — so it
   * has to be abandonable. Everywhere else the sign-in stays modal: those callers
   * open it because they cannot proceed without a user.
   */
  const canDismissSignIn = showModalType === ShowModalType.SIGN_IN && isPartnerRoute(location.pathname)

  const showSignInOverlay = !isLoggined && (showModalType == ShowModalType.SIGN_IN || showModalType == ShowModalType.SIGN_IN_1ST) && isLoadedJsonSuccess
  const showWrapModal = showSignInOverlay || openModalTryPremium.isOpen || showModalType == ShowModalType.QUICK_TUTORIAL || showModalType == ShowModalType.ONBOARDING || showModalType === ShowModalType.WHAT_NEW || showModalType === ShowModalType.MAGIC_DESIGN
  const onCloseOnboarding = async () => {
    if (!userIsPremium && !openModalTryPremium.opened && !isStartupPremiumPromptSuppressed(location.pathname)) {
      let source = location.pathname.includes('create') ? 'Create' : 'Editor Open'

      dispatch(
        setOpenModalTryPremium({
          isOpen: true,
          source: source,
          opened: true,
        }),
      )

      const eventProperties = buildPremiumPromptProperties(
        buildSignupAnalyticsContext({
          source,
          type: 'Metsikon',
          pathname: location.pathname,
        }),
      )
      customAmplitude('Premium Prompt', eventProperties)
      // @ts-ignore
      window.dataLayer.push({ event: 'premium_prompt', ...eventProperties })
    }
  }

  const onCloseModalTutorial = () => {
    // OneSignal push prompt is now called in ModalGoodWork (tutorial completed) 
    // and ModalSkipTutorial/ModalTutorial (tutorial skipped)
  }

  return (
    <ThemeProvider theme={lightTheme}>
      {/* Modals handled by baseui */}
      <ModalUnsuportType />
      {showModalType === ShowModalType.DOWNLOAD_APP && (
        <Suspense fallback={null}>
          <ModalDownloadApp />
        </Suspense>
      )}
      <ModalProjectOpenFailed />
      <ModalProjectAlreadyOpen />
      <ModalConfirmSignOut />
      <ModalDiscardProject />
      <ModalDeleteProject />
      {showModalType === ShowModalType.DOWNLOAD_PROJECT && (
        <Suspense fallback={null}>
          <ModalDownloadProject />
        </Suspense>
      )}
      <ModalNoInternet />
      <IntroVideo />
      <ModalAILimitReached />
      <ModalCustomText />
      <ModalLimitedTimeOffer />
      <ModalLimitedOffer />

      {/* Modals handled by ScaledAppearanceModal */}
      <WrapModal
        id="wrap-modal"
        className={`custom-scroll ${showWrapModal ? (USE_ANIMATIONS ? modalStyles.showWrapModal : modalStyles.showWrapModalNoAnimation) : (USE_ANIMATIONS ? modalStyles.hideWrapModal : modalStyles.hideWrapModalNoAnimation) }`}
        style={{
          zIndex: Z_INDEX.MODAL,
          pointerEvents: showWrapModal ? 'auto' : 'none',
        }}
      >
        <div style={{zIndex: 1000, width: '100%', height: '100%'}}>
          <ScaledAppearanceModal isOpen={showModalType != ShowModalType.ONBOARDING && openModalTryPremium.isOpen} name="premium" useAnimations={USE_ANIMATIONS}>
            <Metsikon handleOpenTutorial={handleOpenTutorial} pendingPlanRef={pendingPlanRef} />
          </ScaledAppearanceModal>
          <ScaledAppearanceModal isOpen={!isLoggined && (showModalType == ShowModalType.SIGN_IN || showModalType == ShowModalType.SIGN_IN_1ST)} name="login" useAnimations={USE_ANIMATIONS}>
            <SignIn
              close={closeSignIn}
              setLoggin={setIsLoggined}
              isOpen={showModalType == ShowModalType.SIGN_IN || showModalType == ShowModalType.SIGN_IN_1ST}
              handleOpenTutorial={handleOpenTutorial}
              isFirstTime={showModalType === ShowModalType.SIGN_IN_1ST}
              canDismiss={canDismissSignIn}
            />
          </ScaledAppearanceModal>
          <ScaledAppearanceModal isOpen={showModalType == ShowModalType.QUICK_TUTORIAL} name="tutorial" useAnimations={USE_ANIMATIONS}>
            <Suspense fallback={null}>
              <ModalTutorial onClosed={onCloseModalTutorial}/>
            </Suspense>
          </ScaledAppearanceModal>
          <ScaledAppearanceModal isOpen={showModalType == ShowModalType.ONBOARDING} name="onboarding" useAnimations={USE_ANIMATIONS}>
            <Suspense fallback={null}>
              <Onboarding onClosed={onCloseOnboarding}/>
            </Suspense>
          </ScaledAppearanceModal>
          {/* WHAT NEW */}
          <ScaledAppearanceModal isOpen={showModalType === ShowModalType.WHAT_NEW} name="what-new" useAnimations={USE_ANIMATIONS}>
            <ModalWhatNew/>
          </ScaledAppearanceModal>
          {/* Magic Design */}
          <ScaledAppearanceModal isOpen={showModalType === ShowModalType.MAGIC_DESIGN} name="magic-design" useAnimations={USE_ANIMATIONS}>
            <Suspense fallback={null}>
              <ModalMagicDesign/>
            </Suspense>
          </ScaledAppearanceModal>
        </div>
      </WrapModal>
    </ThemeProvider>
  )
}

export default ShowModal